import { accentColorConfig } from '@hooks/useAccentColor'
import { Agent, Conversation } from '@typings/dify'
import { AccentColor } from '@typings/index'
import { Check, ChevronLeft, ChevronRight, Edit2, History, Plus, Star, Trash2, X } from 'lucide-react'
import { FC, KeyboardEvent, MouseEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Loader from './Loader'

interface ConversationSidebarProps {
  agent: Agent
  selectedConversation: Conversation | null
  onSelectConversation: (conversation: Conversation) => void
  onDeleteConversation: (conversationId: string) => void
  onToggleFavorite: (conversationId: string) => void
  onRenameConversation: (conversationId: string, newTitle: string) => void
  onNewConversation: () => void
  accentColor: AccentColor
}

const ConversationSidebar = ({
  agent,
  selectedConversation,
  onSelectConversation,
  onDeleteConversation,
  onToggleFavorite,
  onRenameConversation,
  onNewConversation,
  accentColor,
}: ConversationSidebarProps) => {
  const [isOpen, setIsOpen] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [displayedConversations, setDisplayedConversations] = useState<Conversation[]>([])
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false);
  const pageSize = 10 // Number of conversations to load per page
  const sidebarRef = useRef<HTMLDivElement | null>(null)
  const { t } = useTranslation()

  const favoriteConversations = agent.conversations.filter((conv) => conv.isFavorite)
  const otherConversations = displayedConversations.filter((conv) => !conv.isFavorite)

  useEffect(() => {
    const initialConversations = agent.conversations.slice(0, pageSize)
    setDisplayedConversations(initialConversations)
  }, [agent.conversations])

  useEffect(() => {
    const checkScrollable = () => {
      if (sidebarRef.current) {
        const { scrollHeight, clientHeight } = sidebarRef.current
        if (scrollHeight <= clientHeight && displayedConversations.length < agent.conversations.length) {
          loadMoreConversations()
        }
      }
    }

    checkScrollable()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedConversations, agent.conversations])

  // Affiche le loader si les conversations sont en cours de chargement
  useEffect(() => {
    if (!agent.conversations.length) {
      setLoading(true);
    } else {
      setLoading(false);
    }
    // Après 5 secondes, on désactive le loader pour éviter un affichage bloqué avec des agents sans conversations
    setTimeout(() => {
      setLoading(false)
    }, 5000)
  }, [agent.conversations]);

  const loadMoreConversations = () => {
    const nextPage = page + 1
    const nextConversations = agent.conversations.slice(0, nextPage * pageSize)
    setDisplayedConversations(nextConversations)
    setPage(nextPage)
  }

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      loadMoreConversations()
    }
  }

  const handleDoubleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target instanceof HTMLElement && !e.target.closest('button') && !e.target.closest('input')) {
      setIsOpen(!isOpen)
    }
  }

  const handleStartEdit = (conversation: Conversation) => {
    setEditingId(conversation.id)
    setEditTitle(conversation.title)
  }

  const handleSaveEdit = (conversationId: string) => {
    if (editTitle.trim()) {
      onRenameConversation(conversationId, editTitle.trim())
    }
    setEditingId(null)
  }

  return (
    <div
      className={`relative h-full border-r dark:border-gray-700 flex flex-col transition-all duration-300 ${
        isOpen ? 'w-64' : 'w-20'
      }`}
      onDoubleClick={handleDoubleClick}
    >
      <div
        className={`p-4 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex justify-between items-center`}
      >
        <div className="flex items-center justify-between w-full">
          {isOpen && <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('chat.conversations')}</h2>}
          <div className="flex items-center mr-6">
            <button
              onClick={onNewConversation}
              className={`p-2 rounded-lg ${accentColorConfig[accentColor].primary} ${
                accentColorConfig[accentColor].primary.includes('text-black') ? '' : 'text-white'
              } hover:${accentColorConfig[accentColor].hover} transition-colors w-8 h-8 flex items-center justify-center`}
              title={t('chat.new_conversation')}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>

      <div ref={sidebarRef} className="flex-1 overflow-y-auto bg-white dark:bg-gray-900" onScroll={handleScroll}>
        {loading ? (
          <Loader />
        ) : isOpen ? (
          <>
            {favoriteConversations.length > 0 && (
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{t('chat.favorites')}</h3>
                <div className="space-y-1">
                  {favoriteConversations.map((conversation) => (
                    <ConversationItem
                      key={conversation.id}
                      conversation={conversation}
                      isSelected={selectedConversation?.id === conversation.id}
                      onSelect={onSelectConversation}
                      onDelete={onDeleteConversation}
                      onToggleFavorite={onToggleFavorite}
                      onStartEdit={handleStartEdit}
                      editingId={editingId}
                      editTitle={editTitle}
                      setEditTitle={setEditTitle}
                      onSaveEdit={handleSaveEdit}
                      accentColor={accentColor}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="p-4">
              {agent.conversations.length === 0 ? (
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{t('chat.no_conversations')}</h3>
              ) : (
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{t('chat.recent')}</h3>
              )}
              
              <div className="space-y-1">
                {otherConversations.map((conversation) => (
                  <ConversationItem
                    key={conversation.id}
                    conversation={conversation}
                    isSelected={selectedConversation?.id === conversation.id}
                    onSelect={onSelectConversation}
                    onDelete={onDeleteConversation}
                    onToggleFavorite={onToggleFavorite}
                    onStartEdit={handleStartEdit}
                    editingId={editingId}
                    editTitle={editTitle}
                    setEditTitle={setEditTitle}
                    onSaveEdit={handleSaveEdit}
                    accentColor={accentColor}
                  />
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="py-4 flex flex-col items-center space-y-4">
            <button
              onClick={() => onToggleFavorite(selectedConversation?.id || '')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg group relative"
              title="Favoris"
            >
              <Star size={20} className="text-yellow-400" />
              <span className="text-xs mt-1 block dark:text-gray-400">{favoriteConversations.length}</span>
            </button>
            <button
              onClick={() => setIsOpen(true)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg group relative"
              title="Historique"
            >
              <History size={20} className="text-gray-600 dark:text-gray-400" />
              <span className="text-xs mt-1 block dark:text-gray-400">{otherConversations.length}</span>
            </button>
          </div>
        )}
      </div>

      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`absolute top-4 -right-4 z-30 p-2 rounded-full bg-white dark:bg-gray-800 shadow-lg hover:${
          accentColorConfig[accentColor].light
        } dark:hover:bg-gray-700 transition-colors`}
      >
        {isOpen ? (
          <ChevronLeft size={20} className="dark:text-white" />
        ) : (
          <ChevronRight size={20} className="dark:text-white" />
        )}
      </button>
    </div>
  )
}

interface ConversationItemProps {
  conversation: Conversation
  isSelected: boolean
  onSelect: (conversation: Conversation) => void
  onDelete: (id: string) => void
  onToggleFavorite: (id: string) => void
  onStartEdit: (conversation: Conversation) => void
  editingId: string | null
  editTitle: string
  setEditTitle: (title: string) => void
  onSaveEdit: (id: string) => void
  accentColor: AccentColor
}

const ConversationItem: FC<ConversationItemProps> = ({
  conversation,
  isSelected,
  onSelect,
  onDelete,
  onToggleFavorite,
  onStartEdit,
  editingId,
  editTitle,
  setEditTitle,
  onSaveEdit,
  accentColor,
}) => {
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSaveEdit(conversation.id)
    }
    if (e.key === 'Escape') {
      setEditTitle('')
      onSaveEdit(conversation.id)
    }
  }

  return (
    <div
      className={`group flex items-center space-x-2 p-2 rounded-lg relative cursor-pointer ${
        isSelected
          ? `${accentColorConfig[accentColor].light} dark:bg-gray-800`
          : 'hover:bg-gray-50 dark:hover:bg-gray-800'
      }`}
    >
      {editingId === conversation.id ? (
        <div className="flex-1 flex items-center space-x-2">
          <input
            type="text"
            value={editTitle.replace('[Favorite] ', '')}
            onChange={(e) => setEditTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 px-2 py-1 rounded border dark:border-gray-700 dark:bg-gray-900 dark:text-white"
            autoFocus
          />
          <button
            onClick={() => onSaveEdit(conversation.id)}
            className="p-1 hover:text-green-500 dark:text-gray-400 dark:hover:text-green-400"
          >
            <Check size={16} />
          </button>
          <button
            onClick={() => onSaveEdit(conversation.id)}
            className="p-1 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <>
          <div className="flex-1 truncate text-gray-900 dark:text-gray-100" onClick={() => onSelect(conversation)}>
            {conversation.title.replace('[Favorite] ', '')}
          </div>
          <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 absolute right-0 bg-white dark:bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => onStartEdit(conversation)}
              className="p-1 hover:text-blue-500 dark:text-gray-400 dark:hover:text-blue-400"
            >
              <Edit2 size={16} />
            </button>
            <button onClick={() => onToggleFavorite(conversation.id)} className="p-1">
              <Star
                size={16}
                className={
                  conversation.isFavorite
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'hover:text-yellow-400 dark:text-gray-400 dark:hover:text-yellow-400'
                }
              />
            </button>
            <button
              onClick={() => onDelete(conversation.id)}
              className="p-1 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default ConversationSidebar
